/**
 * InfiniteMaxProgressBar
 * - No upper max (conceptually infinite input values)
 * - Background fill animates from 0 → 100 and then stays at 100
 * - If value exceeds 100, ONLY the number readout keeps animating upward
 * - Accessible (role="progressbar" with bounded ARIA values 0..100)
 * - Tailwind for styling
 */
import { FC, useCallback, useMemo } from 'react';

import clsx from 'clsx';
import { useMediaQuery } from 'usehooks-ts';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { ROUTES } from 'shared/constants';

import attention from 'assets/png/!.png?format=webp;avif&as=picture';
import star from 'assets/png/star.png?format=webp;avif&as=picture';

import { InfiniteMaxProgressBarProps } from './progress-bar.types';
import { clamp, useAnimatedNumber } from './progress-bar.utils';

export const InfiniteMaxProgressBar: FC<InfiniteMaxProgressBarProps> = ({
  value,
  decimals = 0,
  animateDuration = 600,
  className = '',
  trackClassName = '',
  fillClassName = '',
  label,
  format,
  showAttention = false,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const widthPct = useMemo(() => clamp(value, 0, 100), [value]);
  const animatedNumber = useAnimatedNumber(
    Number.isFinite(value) ? value : 0,
    animateDuration,
    decimals,
  );
  const isFull = useMemo(() => widthPct >= 100, [widthPct]);

  const clickHandler = useCallback(() => {
    if (value >= 100 && showAttention) {
      window.open(`${window.location.origin}${ROUTES.NFTS}`, '_blank', 'noopener,noreferrer');
    }
  }, [value, showAttention]);

  return (
    <div
      className={clsx('relative w-full select-none', className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(widthPct)}
      aria-label={label}
      onClick={clickHandler}
    >
      <div className="h-8 rounded-full bg-[linear-gradient(180deg,_#202066_0%,_#58588C_100%)] p-0.5 text-base sm:h-14 sm:p-1 sm:text-3xl">
        <div className="h-full w-full rounded-full">
          <div
            className={clsx(
              `relative h-full w-full overflow-hidden rounded-full bg-[linear-gradient(180deg,_#0A0A42_0%,_#020037_100%)] shadow-[inset_0_4px_0_0_rgba(255,255,255,0.15),_-2px_-2px_4px_0_rgba(0,0,0,0.25)]`,
              trackClassName,
            )}
          >
            <div
              className={[
                'absolute left-0 top-0 h-full',
                'bg-[linear-gradient(184deg,_#B36E1B_2.53%,_#373470_96.52%)]',
                'opacity-80 transition-[width] ease-out motion-reduce:transition-none',
                isFull
                  ? 'rounded-full'
                  : 'rounded-l-2xl [clip-path:polygon(0_0,100%_0,calc(100%-_var(--cut))_100%,0_100%)]',
                fillClassName,
              ].join(' ')}
              style={{
                width: `${Math.min(widthPct, 100)}%`,
                transitionDuration: `${animateDuration}ms`,
                ...(isFull ? {} : { ['--cut' as any]: isMobile ? `${16}px` : `${22}px` }),
              }}
            />

            <OptimizedImage
              imageData={star}
              alt="star"
              className="absolute left-0.5 top-1 h-6 w-6 sm:left-1.5 sm:top-1.5 sm:h-10 sm:w-10"
            />

            <div className="absolute inset-0 flex items-center justify-center sm:top-1">
              <span className="bg-gradient-to-b from-white to-[#B5E0FF] bg-clip-text text-center font-bold text-transparent">
                {format ? format(animatedNumber) : animatedNumber.toFixed(decimals)}
                {value < 100 && <>/100</>}
              </span>
            </div>

            {showAttention && value >= 100 && (
              <OptimizedImage
                imageData={attention}
                alt="attention"
                className="absolute right-1 top-1 h-5 w-5 animate-badgePop motion-reduce:animate-none sm:right-1.5 sm:top-2 sm:h-8 sm:w-8"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
